import { Button } from "@peer-start/ui";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { pageDefinitions } from "../../../app/page-references.js";
import { useAppContext } from "../../../app/use-app-context.js";
import { OnboardingStepper } from "../components/onboarding/OnboardingStepper.js";
import { SchoolSetupSteps } from "../components/onboarding/SchoolSetupSteps.js";
import { loadSchoolProfile, saveSchoolProfile, type SchoolProfile } from "../data/school-profile.js";

const steps = ["profile", "culture", "documents"] as const;

export function SchoolSetupPage() {
  const { t } = useTranslation();
  const { persona } = useAppContext();
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const requestedStep = Number(search.get("step") ?? 1);
  const currentStep =
    Number.isInteger(requestedStep) && requestedStep >= 1 && requestedStep <= 3 ? requestedStep - 1 : 0;
  const [profile, setProfile] = useState<SchoolProfile>(loadSchoolProfile);
  if (persona.kind === "employee") return null;

  const patch = (partial: Partial<SchoolProfile>) => setProfile((current) => ({ ...current, ...partial }));
  const goToStep = (index: number) => setSearch({ step: String(index + 1) });
  const stepLabels = steps.map((id) => ({ id, label: t(`school.steps.${id}`) }));
  const firstStepComplete = Boolean(profile.schoolName && profile.foundation && profile.region && profile.staffRoles.length);
  const secondStepComplete = Boolean(
    profile.educationalType && profile.organisationType && profile.schoolType && profile.specification,
  );
  const canContinue = currentStep === 0 ? firstStepComplete : currentStep === 1 ? secondStepComplete : true;

  const saveAndContinue = () => {
    saveSchoolProfile(profile);
    if (currentStep < steps.length - 1) goToStep(currentStep + 1);
    else navigate(pageDefinitions.school.path);
  };

  return (
    <div className="flex min-h-[calc(100dvh-7rem)] flex-col">
      <div className="mx-auto w-full max-w-3xl flex-1 pb-20">
        <div className="mb-6 flex justify-end">
          <button
            className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
            data-control-id="school.setup.close"
            onClick={() => navigate(pageDefinitions.school.path)}
            type="button"
          >
            {t("school.setup.close")}
          </button>
        </div>
        <OnboardingStepper currentStep={currentStep} label={t("school.setup.stepperLabel")} steps={stepLabels} />
        <SchoolSetupSteps currentStep={currentStep} patch={patch} profile={profile} />
      </div>
      <div className="sticky bottom-0 mt-10 border-t border-border bg-background pt-4 pb-2">
        <div className="flex items-center justify-between gap-3">
          <Button
            controlId="school.setup.previous"
            disabled={currentStep === 0}
            onClick={() => goToStep(Math.max(0, currentStep - 1))}
            type="button"
            variant="secondary"
          >
            {t("school.setup.previous")}
          </Button>
          <Button controlId="school.setup.next" disabled={!canContinue} onClick={saveAndContinue} type="button">
            {t(currentStep === steps.length - 1 ? "school.setup.save" : "school.setup.next")}
          </Button>
        </div>
      </div>
    </div>
  );
}
